import CandidateService from '@/services/candidate-service'

export const RESUME_ACCEPT = 'application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf, text/plain, application/rtf, text/markdown'
export const RESUME_MAX_BYTES = 1690000

const ALLOWED_EXT = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.md']

export function validateResumeFile(file) {
  if (!file) return 'Please choose a resume file.'
  if (file.size > RESUME_MAX_BYTES) return 'File Size Should be less than 1.5 mb'
  const name = String(file.name || '').toLowerCase()
  const extOk = ALLOWED_EXT.some(ext => name.endsWith(ext))
  if (!extOk) return 'Please upload a PDF, DOC, or DOCX resume.'
  return null
}

function buildUpdatedBy() {
  let userData = {}
  try {
    userData = JSON.parse(localStorage.getItem('userData') || '{}') || {}
  } catch (err) {
    userData = {}
  }
  const title = `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
  return `[{"title" : "${title}", "emp_id": "${userData.emp_id || ''}"}]`
}

export function sanitizeResumeFilename(name) {
  const raw = String(name || 'resume.pdf')
  const extMatch = raw.match(/(\.(pdf|doc|docx|txt|rtf|md))$/i)
  const ext = extMatch ? extMatch[1].toLowerCase() : '.pdf'
  const base = raw
    .replace(/(\.(pdf|doc|docx|txt|rtf|md))$/i, '')
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80)
  return `${base || 'resume'}${ext}`
}

function appendNonEmpty(formData, key, value) {
  const str = String(value || '').trim()
  if (!str || str === 'null' || str === 'undefined') return
  formData.append(key, str)
}

function appendIdentity(formData, candidate) {
  if (!candidate || typeof candidate !== 'object') return
  appendNonEmpty(formData, 'first_name', candidate.first_name || candidate.firstName)
  appendNonEmpty(formData, 'last_name', candidate.last_name || candidate.lastName)
  const email = String(candidate.email || '').trim().toLowerCase()
  if (email.includes('@')) formData.append('email', email)
  const contactRaw = candidate.contact_no != null ? candidate.contact_no : candidate.contact
  const digits = String(contactRaw || '').replace(/\D/g, '')
  if (digits) formData.append('contact_no', digits)
}

export function buildResumeOnlyFormData(file, candidate) {
  const formData = new FormData()
  // Text fields first — multer filename() often reads req.body before the file part.
  appendIdentity(formData, candidate)
  formData.append('updated_by', buildUpdatedBy())
  const filename = sanitizeResumeFilename(file && file.name)
  formData.append('cvFile', file, filename)
  return formData
}

function isHtmlErrorBody(value) {
  if (typeof value !== 'string') return false
  const head = value.trim().slice(0, 240).toLowerCase()
  return (
    head.startsWith('<!doctype')
    || head.startsWith('<html')
    || head.includes('bad gateway')
    || head.includes('nginx')
  )
}

function safeErrorText(value) {
  if (value == null) return ''
  if (typeof value !== 'string') return ''
  if (isHtmlErrorBody(value)) return ''
  const trimmed = value.replace(/\s+/g, ' ').trim()
  return trimmed.slice(0, 180)
}

export function extractCandidateUpdateError(res) {
  const fallback = 'Resume upload failed. Please try again.'
  if (!res) return fallback
  const status = Number(res.status)
  if (status === 413) return 'File Size is greater than 1 mb'
  if (status === 502 || status === 503 || isHtmlErrorBody(res) || isHtmlErrorBody(res.data)) {
    return 'Resume could not be saved. The server rejected the upload — try a PDF under 1.5 MB.'
  }
  if (typeof res === 'string') return safeErrorText(res) || fallback
  const data = res.data
  if (typeof data === 'string') return safeErrorText(data) || fallback
  const payload = data && typeof data === 'object' ? data : {}
  const errors = payload.error && payload.error.errors
  const first = Array.isArray(errors) ? errors[0] : null
  const firstMsg = first && (first.message || (typeof first === 'string' ? first : ''))
  const fromFirst = safeErrorText(firstMsg)
  if (fromFirst) return fromFirst
  const fromMessage = safeErrorText(payload.message)
  if (fromMessage) return fromMessage
  const fromError = safeErrorText(typeof payload.error === 'string' ? payload.error : '')
  if (fromError) return fromError
  return fallback
}

export function extractCvPath(res) {
  if (!res) return null
  const payload = res.data !== undefined && res.data !== null && typeof res.data === 'object'
    ? res.data
    : res
  const row = Array.isArray(payload) ? payload[0] : payload
  if (!row || typeof row !== 'object') return null
  return row.cv_path || row.cv || null
}

export function resumeFileNameFromPath(path) {
  if (!path) return ''
  const parts = String(path).split('/')
  return parts[parts.length - 1] || path
}

function normalizeStatus(status) {
  return String(status || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
}

/**
 * Closed / terminal outcomes used across CandidatesView + StatusWiseTracker pipeline.
 * In Action = still being worked (not these).
 */
const CLOSED_STATUS_KEYS = [
  'rejected',
  'screenreject',
  'reviewreject',
  'joined',
  'offerdrop',
  'notoffered',
  'drop',
  'attrition',
  'earlyattrition',
  'duplicate',
  'noshow',
  'notinterested',
  'notrelevant',
].reduce((acc, key) => {
  acc[key] = true
  return acc
}, {})

export function candidateRowStatus(row) {
  if (!row) return ''
  return row.candidateStatus || row.status || ''
}

export function isClosedCandidateStatus(status) {
  const key = normalizeStatus(status)
  return !!key && !!CLOSED_STATUS_KEYS[key]
}

/** In Action: any job status that is not a closed/terminal outcome. */
export function isInActionCandidateStatus(status) {
  const key = normalizeStatus(status)
  if (!key) return false
  return !CLOSED_STATUS_KEYS[key]
}

export function isInActionCandidate(row) {
  return isInActionCandidateStatus(candidateRowStatus(row))
}

export function uploadCandidateResumeOnly(candidateId, file, candidate) {
  const formData = buildResumeOnlyFormData(file, candidate)
  return CandidateService.uploadCandidateResume(candidateId, formData)
}
